'use client';

import { Trans, useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LocaleLink } from '@/components/locale-link';

const MCP_URL = 'https://mcp.firmendata.com/mcp';
const SUPPORT_EMAIL = 'info@firmendata.com';

type ToolKey =
  | 'autocomplete_companies'
  | 'search_companies'
  | 'get_company'
  | 'get_company_history'
  | 'get_company_financials'
  | 'get_company_shareholders'
  | 'get_company_ubo';

type Tool = {
  key: ToolKey;
};

const TOOLS: Tool[] = [
  { key: 'autocomplete_companies' },
  { key: 'search_companies' },
  { key: 'get_company' },
  { key: 'get_company_history' },
  { key: 'get_company_financials' },
  { key: 'get_company_shareholders' },
  { key: 'get_company_ubo' },
];

const USE_CASES = [
  'targetScreening',
  'targetDeepDive',
  'kyc',
  'benchmarking',
] as const;

const LIMITATIONS = [
  'germanOnly',
  'searchCap',
  'realtimeLatency',
  'capTableCoverage',
  'historyGaps',
] as const;

const TROUBLESHOOTING = [
  'oauth',
  'rateLimit',
  'unknownEuId',
  'coverage',
  'tokenExpired',
] as const;

const TOC_KEYS = [
  'overview',
  'setup',
  'auth',
  'tools',
  'examples',
  'pricing',
  'limitations',
  'troubleshooting',
  'support',
] as const;

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="mb-4 text-2xl font-semibold tracking-tight">{title}</h2>
      <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

export default function McpDocsPage() {
  const { t } = useTranslation();

  // Inline rich-text components for the Trans component
  const strongInline = <strong className="text-foreground" />;
  const codeInline = (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm" />
  );

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-12 md:py-16">
      {/* Hero */}
      <header className="mb-12 border-b border-border pb-10">
        <Badge variant="secondary" className="mb-4">
          {t('mcp.hero.badge')}
        </Badge>
        <h1 className="text-4xl font-bold tracking-tight md:text-5xl">
          {t('mcp.hero.title')}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground md:text-xl">
          {t('mcp.hero.subtitle')}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild>
            <a
              href="https://claude.ai/settings/connectors"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('mcp.hero.addCta')}
            </a>
          </Button>
          <Button asChild variant="outline">
            <LocaleLink href="/plans">{t('mcp.hero.plansCta')}</LocaleLink>
          </Button>
        </div>
      </header>

      {/* Table of contents */}
      <nav className="mb-12 rounded-xl border border-border bg-muted/40 p-5 text-sm">
        <p className="mb-2 font-medium text-foreground">{t('mcp.toc.label')}</p>
        <ul className="grid gap-1 md:grid-cols-2">
          {TOC_KEYS.map((key) => (
            <li key={key}>
              <a
                href={`#${key}`}
                className="text-muted-foreground hover:text-foreground hover:underline"
              >
                {t(`mcp.toc.${key}`)}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="space-y-14">
        {/* Overview */}
        <Section id="overview" title={t('mcp.overview.title')}>
          <p>{t('mcp.overview.p1')}</p>
          <p>{t('mcp.overview.p2')}</p>
        </Section>

        {/* Setup */}
        <Section id="setup" title={t('mcp.setup.title')}>
          <ol className="ml-6 list-decimal space-y-3">
            <li>
              <Trans
                i18nKey="mcp.setup.step1"
                components={{
                  bold: strongInline,
                  signupLink: (
                    <LocaleLink
                      href="/signup"
                      className="text-primary underline-offset-4 hover:underline"
                    />
                  ),
                  pricingLink: <a href="#pricing" className="text-primary hover:underline" />,
                }}
              />
            </li>
            <li>
              <Trans
                i18nKey="mcp.setup.step2"
                components={{ bold: strongInline }}
              />
            </li>
            <li>
              <Trans
                i18nKey="mcp.setup.step3Lead"
                components={{ bold: strongInline }}
              />
              <div className="mt-3 space-y-3">
                <div className="rounded-lg border border-border bg-card p-4">
                  <p className="text-sm font-semibold text-foreground">
                    {t('mcp.setup.step3CustomTitle')}
                  </p>
                  <p className="mt-1 text-sm">
                    <Trans
                      i18nKey="mcp.setup.step3CustomBody"
                      components={{ bold: strongInline }}
                    />
                  </p>
                  <pre className="mt-3 overflow-x-auto rounded-md bg-muted p-3 font-mono text-sm text-foreground">
                    {MCP_URL}
                  </pre>
                </div>
                <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-foreground">
                      {t('mcp.setup.step3DirectoryTitle')}
                    </p>
                    <Badge variant="secondary">
                      {t('navigation.comingSoon')}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm">
                    <Trans
                      i18nKey="mcp.setup.step3DirectoryBody"
                      components={{ bold: strongInline }}
                    />
                  </p>
                </div>
              </div>
            </li>
            <li>
              <Trans
                i18nKey="mcp.setup.step4"
                components={{ bold: strongInline }}
              />
            </li>
            <li>
              <Trans
                i18nKey="mcp.setup.step5"
                components={{
                  bold: strongInline,
                  toolsLink: <a href="#tools" className="text-primary hover:underline" />,
                }}
              />
            </li>
          </ol>
        </Section>

        {/* Authentication */}
        <Section id="auth" title={t('mcp.auth.title')}>
          <p>
            <Trans
              i18nKey="mcp.auth.p1"
              components={{ bold: strongInline, code: codeInline }}
            />
          </p>
          <p>{t('mcp.auth.p2')}</p>
        </Section>

        {/* Tools */}
        <Section id="tools" title={t('mcp.tools.title')}>
          <p>
            <Trans
              i18nKey="mcp.tools.intro"
              components={{ code: codeInline }}
            />
          </p>
          <div className="grid gap-4">
            {TOOLS.map((tool) => (
              <Card key={tool.key}>
                <CardHeader>
                  <CardTitle className="font-mono text-base">
                    {tool.key}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="mb-2 font-medium text-foreground">
                    {t(`mcp.tools.list.${tool.key}.summary`)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t(`mcp.tools.list.${tool.key}.detail`)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </Section>

        {/* Examples */}
        <Section id="examples" title={t('mcp.examples.title')}>
          <p>{t('mcp.examples.intro')}</p>
          <div className="space-y-3">
            {USE_CASES.map((key) => (
              <Card key={key}>
                <CardContent className="py-4">
                  <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-foreground">
                    {t(`mcp.examples.list.${key}.label`)}
                  </p>
                  <p className="italic text-muted-foreground">
                    {t(`mcp.examples.list.${key}.prompt`)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </Section>

        {/* Pricing */}
        <Section id="pricing" title={t('mcp.pricing.title')}>
          <p>
            <Trans
              i18nKey="mcp.pricing.intro"
              components={{
                bold: strongInline,
                usageLink: (
                  <LocaleLink
                    href="/account/usage"
                    className="font-semibold text-primary underline underline-offset-4"
                  />
                ),
              }}
            />
          </p>
          <p>{t('mcp.pricing.rateLimits')}</p>
          <p>
            <Trans
              i18nKey="mcp.pricing.plansLink"
              components={{
                pricingLink: (
                  <LocaleLink
                    href="/plans"
                    className="font-semibold text-primary underline underline-offset-4"
                  />
                ),
              }}
            />
          </p>
        </Section>

        {/* Limitations */}
        <Section id="limitations" title={t('mcp.limitations.title')}>
          <ul className="ml-6 list-disc space-y-2">
            {LIMITATIONS.map((key) => (
              <li key={key}>
                <strong className="text-foreground">
                  {t(`mcp.limitations.items.${key}.label`)}
                </strong>{' '}
                {t(`mcp.limitations.items.${key}.body`)}
              </li>
            ))}
          </ul>
        </Section>

        {/* Troubleshooting */}
        <Section id="troubleshooting" title={t('mcp.troubleshooting.title')}>
          <div className="space-y-4">
            {TROUBLESHOOTING.map((key) => (
              <div key={key}>
                <p className="font-semibold text-foreground">
                  {t(`mcp.troubleshooting.items.${key}.q`)}
                </p>
                <p>{t(`mcp.troubleshooting.items.${key}.a`)}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Support */}
        <Section id="support" title={t('mcp.support.title')}>
          <p>
            <Trans
              i18nKey="mcp.support.p1"
              values={{ email: SUPPORT_EMAIL }}
              components={{
                emailLink: (
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="text-primary underline-offset-4 hover:underline"
                  />
                ),
              }}
            />
          </p>
          <p>
            <Trans
              i18nKey="mcp.support.p2"
              components={{
                restLink: (
                  <LocaleLink
                    href="/developer/apis"
                    className="text-primary underline-offset-4 hover:underline"
                  />
                ),
              }}
            />
          </p>
        </Section>
      </div>
    </div>
  );
}
